import React, { useEffect } from "react";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import styled from "styled-components/native";
import FilterScreen from "./FilterScreen";
import SearchResultScreen from "@scenes/searchResults/SearchResultScreen";
import { View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { getDrawerWidth } from "@navigations/utils/constants";
import { useTheme } from "styled-components/native";
import useProductArray from "@components/common/hooks/useProductArray";
import { ProductCategory, ProductPrefixCategory } from "@utils/constants";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import Routes from "@navigations/Routes";
import {
  setAdHeight,
  setSplitFilterVisible,
} from "@redux/slices/appStateSlice";
import { usePrivateAppGuard } from "@utils/usePrivateAppGuard";
import Breadcrumbs from "@components/molecules/Breadcrumbs/Breadcrumbs";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { css } from "styled-components/native";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import {
  getEffectiveEnterSecondFlow,
  isJewelryProductType,
  needsSettingSearchFirstFlowParamCleanup,
} from "@scenes/searchResults/utils/stripJewelryLinkageFromPayload";
import FilterSplitScreenPortalHost from "./FilterSplitScreenPortalHost";
import { useSearchFiltersAuxPayload } from "@redux/slices/searchFilters";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useAppSettings } from "@redux/slices/globalSlice";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { DefaultTheme } from "styled-components/native";

const getProductTypeFromPrefix = (
  productPrefix: string,
  allowedStockItems?: string[]
): ProductCategory => {
  switch (productPrefix) {
    case ProductPrefixCategory.LAB_GROWN_DIAMOND:
      return ProductCategory.LAB_GROWN_DIAMOND;
    case ProductPrefixCategory.GEMSTONE:
      return ProductCategory.GEMSTONE;
    case ProductPrefixCategory.JEWELRY:
      return ProductCategory.JEWELRY;
    case ProductPrefixCategory.LAB_GROWN_JEWELRY:
      return ProductCategory.LABGROWN_JEWELRY;
    case ProductPrefixCategory.DIAMOND:
      return ProductCategory.DIAMOND;
    default:
      return (
        (allowedStockItems?.[0] as ProductCategory) ?? ProductCategory.DIAMOND
      );
  }
};

const FilterSplitScreen = ({
  productType: productTypeProp,
  fromResultScreen: fromResultScreenProp,
  sectionName: sectionNameProp,
  fromNewFilterScreen: fromNewFilterScreenProp,
  filterSplitStep: filterSplitStepProp,
}: any) => {
  const getAppSettings = useAppSettings();
  const iframeFlowV2 = getAppSettings?.iframe_flow_v2 === true;
  const { skipFilterScreen, showFiltersOnStepTwo } = useProductArray();
  const theme = useTheme();
  const routes = useRoute<RouteProp<any>>();
  const navigation = useNavigation();
  const { deviceWidth: screenWidth } = useIsFoldable();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { shouldBlock: shouldBlockPublicAccess } = usePrivateAppGuard();
  const payload = useSearchFiltersAuxPayload();
  const userInfo = useUserInfo();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const dispatch = useDispatch();
  const isSplitFilterVisible = useSelector(
    (state: any) => state.appState?.isSplitFilterVisible
  );
  const disableJewelrySettingStoneLinkage = useSelector(
    (state: any) => state.appState?.disableJewelrySettingStoneLinkage
  );
  const mRouteParams = routes?.params;
  const searchForRing = mRouteParams?.searchForRing;
  const productPrefix = mRouteParams?.productPrefix;
  const productType =
    productTypeProp ||
    mRouteParams?.productType ||
    getProductTypeFromPrefix(productPrefix, userInfo?.getAllowedStockTypes());
  const enterSecondFlow = getEffectiveEnterSecondFlow(
    mRouteParams?.enterSecondFlow,
    productType,
    disableJewelrySettingStoneLinkage
  );
  const saved_searches = mRouteParams?.saved_search;
  const fromResultScreen =
    mRouteParams?.fromResultScreen || fromResultScreenProp || false;
  const sectionNameRedux = useSelector(
    (state: any) => state.searchFilters.sectionName
  );
  const sectionName = routes.params?.sectionName
    ? routes.params?.sectionName
    : sectionNameProp ||
      sectionNameRedux ||
      (productType === ProductCategory.LABGROWN_JEWELRY
        ? SearchFilterTypeEnum.LAB_GROWN_JEWELRY
        : productType === ProductCategory.JEWELRY
        ? SearchFilterTypeEnum.JEWELRY
        : SearchFilterTypeEnum.SINGLE_STONES);
  const toggleFilterScreen = () => {
    dispatch(setSplitFilterVisible(!isSplitFilterVisible));
    dispatch(setAdHeight(0));
  };

  const routeStep = mRouteParams?.filterSplitStep || filterSplitStepProp || 2;
  const fromNewFilterScreen =
    mRouteParams?.fromNewFilterScreen || fromNewFilterScreenProp || false;
  const skipFilterScreenForWeb = theme.isLargeScreen
    ? skipFilterScreen(productType)
    : false;
  const isFilterVisibleOnStepTwo =
    theme.isLargeScreen || theme.isTablet
      ? showFiltersOnStepTwo(productType)
      : false;
  const step = routeStep || (fromResultScreen ? 2 : 1);

  useEffect(() => {
    if (mRouteParams?.fromBarcodeScanner) {
      dispatch(setSplitFilterVisible(false));
      return;
    }
    if (isSplitFilterVisible === null) {
      dispatch(setSplitFilterVisible(true));
    }
  }, []);

  useEffect(() => {
    if (mRouteParams?.fromBarcodeScanner) return;
    if (
      !disableJewelrySettingStoneLinkage ||
      !isJewelryProductType(productType) ||
      !needsSettingSearchFirstFlowParamCleanup(mRouteParams)
    ) {
      return;
    }
    navigation.setParams({
      enterSecondFlow: false,
      saved_search: undefined,
      searchForRing: undefined,
      searchForPendant: undefined,
    });
  }, [
    disableJewelrySettingStoneLinkage,
    productType,
    mRouteParams,
    navigation,
  ]);

  useEffect(() => {
    if (
      productType === ProductCategory.JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY
    ) {
      if (
        isFilterVisibleOnStepTwo !== isSplitFilterVisible &&
        !mRouteParams?.fromBarcodeScanner
      ) {
        dispatch(setSplitFilterVisible(isFilterVisibleOnStepTwo));
      }
    }
  }, [isFilterVisibleOnStepTwo]);

  const isIframe = !!userInfo.isIFrame() && iframeFlowV2;
  const filterContainerWidth = getDrawerWidth(
    screenWidth,
    theme,
    Routes.FilterSplitScreen
  );

  const searchResultContainerWidth = isIframe
    ? screenWidth
    : screenWidth - getDrawerWidth(screenWidth, theme);

  const hideFilters =
    isIframe ||
    (userInfo.enableSellReturnButton() &&
      (payload[sectionName]?.with_available_items === "true" ||
        payload[sectionName]?.with_available_items === true));

  if (shouldBlockPublicAccess) {
    return <View />;
  }
  // Important: avoid mounting `FilterScreen` when the left pane is hidden.
  // `display: none` alone keeps the React tree alive and can keep running TabView/scenes/effects.
  const isFilterPaneVisible = isIframe
    ? false
    : !hideFilters && (isSplitFilterVisible ?? isFilterVisibleOnStepTwo);

  return (
    <>
      {theme.isTablet && enableJewelryBuilder && history.length >= 2 && (
        <>
          <View
            style={{
              position: "absolute",
              top: 100,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: theme["core-01"],
              borderBottomColor: theme["core-03"],
              borderBottomWidth: 1,
            }}
          >
            <Breadcrumbs />
          </View>
        </>
      )}

      <FilterSplitScreenPortalHost name={`TopBarPortal-${productType}`} />

      {!theme.isTablet && enableJewelryBuilder && <Breadcrumbs />}
      {theme.isMobile ||
      (productType === ProductCategory.JEWELRY && step === 1) ? (
        <FilterScreen
          productType={productType}
          fromResultScreen={fromResultScreen}
          filterContainerWidth={filterContainerWidth}
          skipFilterScreenForWeb={skipFilterScreenForWeb}
          showScrollbars={false}
          enterSecondFlow={enterSecondFlow}
          fromSplitScreen={true}
        />
      ) : (
        <Overlay history={history}>
          {!isIframe && (
            <FilterContainer
              width={filterContainerWidth}
              display={
                hideFilters
                  ? "none"
                  : isSplitFilterVisible ?? isFilterVisibleOnStepTwo
                  ? "flex"
                  : "none"
              }
            >
              {isFilterPaneVisible ? (
                <ContainerWrapper1>
                  <FilterScreen
                    productType={productType}
                    fromResultScreen={true}
                    filterContainerWidth={filterContainerWidth}
                    skipFilterScreenForWeb={skipFilterScreenForWeb}
                    isOnSplitScreen={isFilterPaneVisible}
                    showScrollbars={false}
                    enterSecondFlow={enterSecondFlow}
                    key={`portal-${productType}`}
                    fromSplitScreen={true}
                  />
                </ContainerWrapper1>
              ) : null}
            </FilterContainer>
          )}
          {!theme.isLargeScreen && !isIframe && <Separator history={history} />}
          <SearchResultContainer width={searchResultContainerWidth}>
            <ContainerWrapper>
              <SearchResultScreen
                productType={productType}
                fromNewFilterScreen={fromNewFilterScreen}
                toggleFilterScreen={toggleFilterScreen}
                searchResultContainerWidth={searchResultContainerWidth}
                isFilterVisible={
                  hideFilters
                    ? false
                    : isSplitFilterVisible ?? isFilterVisibleOnStepTwo
                }
                sectionName={sectionName}
                skipFilterScreenForWeb={skipFilterScreenForWeb}
                saved_searches={saved_searches}
                deepLinkNonce={mRouteParams?.deepLinkNonce}
                collection={mRouteParams?.collection}
                showFilter={
                  hideFilters
                    ? false
                    : isSplitFilterVisible ?? isFilterVisibleOnStepTwo
                }
                searchForRing={searchForRing}
                enterSecondFlow={enterSecondFlow}
                fromBarcodeScanner={mRouteParams?.fromBarcodeScanner}
                fromResultScreen={fromResultScreen}
                key={`portal-${productType}`}
              />
            </ContainerWrapper>
          </SearchResultContainer>
        </Overlay>
      )}
    </>
  );
};

const Overlay = styled.View<any>`
  flex-direction: row;
  flex-grow: 1;
  overflow: hidden;
  ${({ theme, history }) =>
    theme.isTablet &&
    history.length >= 2 &&
    css`
      margin-top: 50px;
    `}
`;

const FilterContainer = styled.View<{ width: number; display: string }>`
  width: ${({ width }) => width}px;
  display: ${({ display }) => display};
`;

const ContainerWrapper1 = styled.View`
  flex: 1;
  display: flex;
`;

const ContainerWrapper = styled.View`
  flex: 1;
  display: flex;
`;

const SearchResultContainer = styled.View`
  flex: 1;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;

const Separator = styled.View<any>`
  width: 1px;
  background: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  height: 100%;
  ${({ theme, history }) =>
    theme.isTablet &&
    history.length >= 2 &&
    css`
      margin-top: 50px;
    `}
`;

export default FilterSplitScreen;
